/**
 * Resizing an object that is turned — pure geometry, no DOM.
 *
 * A turned object is resized in its OWN axes, the way Figma does it: the handles sit on the
 * turned box, the pointer is read along the object's sides, and the edge or corner opposite
 * the handle stays where it is on the canvas. The resize maths of DraggableItem keeps running
 * on the object's unturned styles; these helpers translate the pointer into that space and
 * the result back out of it.
 */

/** The linear part of a CSS transform — `matrix(a, b, c, d, …)`, translation left out. */
export type LinearTransform = {
    a: number
    b: number
    c: number
    d: number
}

export type Point = {
    x: number
    y: number
}

export const IDENTITY: LinearTransform = { a: 1, b: 0, c: 0, d: 1 }

/** Below this, a skew or rotation term is float noise from a transform that is really 0°. */
const EPSILON = 1e-6

/** `outer` applied after `inner`. */
export const multiplyTransforms = (
    outer: LinearTransform,
    inner: LinearTransform
): LinearTransform => ({
    a: outer.a * inner.a + outer.c * inner.b,
    b: outer.b * inner.a + outer.d * inner.b,
    c: outer.a * inner.c + outer.c * inner.d,
    d: outer.b * inner.c + outer.d * inner.d,
})

export const applyTransform = (m: LinearTransform, p: Point): Point => ({
    x: m.a * p.x + m.c * p.y,
    y: m.b * p.x + m.d * p.y,
})

/**
 * Does the transform leave the object's own x along the canvas x? A flip or a scale does, so
 * the box measured off the screen is still the object's own box and the ordinary resize
 * works. Any rotation does not — a quarter turn included: its sides are horizontal and
 * vertical again, but its width now runs down the canvas, so dragging the right handle
 * would change its height.
 */
export const isAxisAligned = (m: LinearTransform): boolean =>
    Math.abs(m.b) < EPSILON && Math.abs(m.c) < EPSILON

/**
 * A pointer movement on the canvas, along the object's own sides — what the handle would
 * have moved had the object not been turned. Unchanged when the transform cannot be undone.
 */
export const toLocalDelta = (m: LinearTransform, delta: Point): Point => {
    const det = m.a * m.d - m.b * m.c
    if (Math.abs(det) < EPSILON) return delta
    return {
        x: (m.d * delta.x - m.c * delta.y) / det,
        y: (-m.b * delta.x + m.a * delta.y) / det,
    }
}

/**
 * Where `transform-origin` sits inside a box of the given size. Only the keywords the editor
 * offers (see transform.styles); anything else is treated as the CSS default, the centre.
 */
export const transformOriginPoint = (origin: unknown, width: number, height: number): Point => {
    const words = String(origin ?? "center").toLowerCase()
    const x = words.includes("left") ? 0 : words.includes("right") ? width : width / 2
    const y = words.includes("top") ? 0 : words.includes("bottom") ? height : height / 2
    return { x, y }
}

export type BoxT = {
    left: number
    top: number
    width: number
    height: number
}

/**
 * Where a resized box has to go so that the object turns out exactly as the handle dragged
 * it on the canvas.
 *
 * `resized` is the box the ordinary resize worked out in the object's own, unturned axes —
 * with the edge opposite the handle kept in place THERE. Once the object's own transform
 * (turning about its transform origin) is applied, that edge would drift, because the
 * origin moves with the size. Solving `L + O + M·(p − O)` for the new top-left so that every
 * point of the resized box lands where the old transform put it gives:
 *
 *     L₁ = L₀ + O₀ − O₁ + M · (d + O₁ − O₀),   d = resized top-left − original top-left
 *
 * which is `resized` itself when the object is not turned (M = identity).
 *
 * @param own the object's OWN transform, not including its parents' — the styles live in
 *        the parent's space, and the parents' transforms apply to both sides alike
 */
export const anchorResizedBox = (
    original: BoxT,
    resized: BoxT,
    own: LinearTransform,
    origin: unknown
): BoxT => {
    const o0 = transformOriginPoint(origin, original.width, original.height)
    const o1 = transformOriginPoint(origin, resized.width, resized.height)
    const shift = applyTransform(own, {
        x: resized.left - original.left + o1.x - o0.x,
        y: resized.top - original.top + o1.y - o0.y,
    })
    const round = (value: number) => Math.round(value * 100) / 100
    return {
        ...resized,
        left: round(original.left + o0.x - o1.x + shift.x),
        top: round(original.top + o0.y - o1.y + shift.y),
    }
}

/** For a `transform` property — the linear part only, the chrome is positioned by top/left. */
export const toCssMatrix = (m: LinearTransform): string =>
    `matrix(${m.a}, ${m.b}, ${m.c}, ${m.d}, 0, 0)`

/** The built-in double arrows, one per 45° — the nearest one stands in for any angle. */
const RESIZE_CURSOR_KEYWORDS = ["ew-resize", "nwse-resize", "ns-resize", "nesw-resize"]

const nearestResizeKeyword = (degrees: number): string => {
    const halfTurn = ((degrees % 180) + 180) % 180
    return RESIZE_CURSOR_KEYWORDS[Math.round(halfTurn / 45) % 4]
}

/** Rendered size of the resize cursor in px — the size of the system arrows. */
const RESIZE_CURSOR_SIZE = 24

/**
 * The resize cursor of every handle: a slim black double arrow with a white halo, the way
 * Figma draws it, pointing exactly along `degrees`. One drawing for every angle, so a turned
 * object's handles look the same as an upright one's — the built-in arrows only know four
 * directions and differ per system. Hot spot in the middle; the nearest built-in arrow is the
 * fallback.
 */
export const resizeCursor = (degrees: number): string => {
    const angle = Math.round(degrees * 10) / 10
    const arrow = "M5 16 L10 11 L10 15 L22 15 L22 11 L27 16 L22 21 L22 17 L10 17 L10 21 Z"
    const size = RESIZE_CURSOR_SIZE
    const svg =
        `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' ` +
        `viewBox='0 0 32 32'><g transform='rotate(${angle} 16 16)'>` +
        // the halo first, the arrow on top of it
        `<path d='${arrow}' fill='white' stroke='white' stroke-width='3.5' ` +
        `stroke-linejoin='round'/><path d='${arrow}' fill='black'/></g></svg>`
    const hotSpot = size / 2
    return `url("data:image/svg+xml,${encodeURIComponent(
        svg
    )}") ${hotSpot} ${hotSpot}, ${nearestResizeKeyword(degrees)}`
}

/**
 * Where a point of a turned box is on the canvas — `fx`, `fy` are fractions of its width and
 * height (0 = left / top, 1 = right / bottom), so a handle is e.g. (1, 0.5) for the right edge.
 * `box` is the unturned box around the point it is drawn at (see measureOrientedChrome).
 */
export const orientedPoint = (box: BoxT, m: LinearTransform, fx: number, fy: number): Point => {
    const offset = applyTransform(m, {
        x: (fx - 0.5) * box.width,
        y: (fy - 0.5) * box.height,
    })
    return {
        x: box.left + box.width / 2 + offset.x,
        y: box.top + box.height / 2 + offset.y,
    }
}

/** sin of the shallowest angle a handle's path may cross a guide at and still snap to it (30°) */
const MIN_CROSSING = 0.5

/**
 * An edge handle of a turned object only travels along `axis` (the object's own side turned
 * onto the canvas), so a snap that pulled its canvas x or y onto a guide has to be turned back
 * into a distance along that axis: where the handle's path meets the guide.
 *
 * @param snappedDelta the canvas delta after snapping, and which canvas axes it snapped
 * @param rawDistance how far along `axis` the pointer alone would take the handle
 * @returns the distance along `axis` that lands on a guide — the one nearest the pointer —
 *          or null when no guide can be reached along the axis
 */
export const snapDistanceAlongAxis = (
    axis: Point,
    snappedDelta: { delta: Point; snapped: { x: boolean; y: boolean } },
    rawDistance: number
): number | null => {
    /**
     * A guide the path crosses at a shallow angle is skipped: pulling the handle a few px
     * across it would slide it far along the path, a jump rather than a snap.
     */
    const length = Math.hypot(axis.x, axis.y)
    const crosses = (component: number) => Math.abs(component) >= MIN_CROSSING * length
    const candidates: number[] = []
    if (snappedDelta.snapped.x && crosses(axis.x)) candidates.push(snappedDelta.delta.x / axis.x)
    if (snappedDelta.snapped.y && crosses(axis.y)) candidates.push(snappedDelta.delta.y / axis.y)
    if (!candidates.length) return null
    return candidates.reduce((best, candidate) =>
        Math.abs(candidate - rawDistance) < Math.abs(best - rawDistance) ? candidate : best
    )
}

/**
 * What undoes the stretch and shear of `m` but keeps its turn — for something drawn inside a
 * chrome drawn through `m` that must stay a plain square turned with the object's side: a
 * corner handle. A sheared object (a turned member stretched along with its multiselection)
 * would otherwise squash its handles into thin rhombi.
 */
export const uprightCounterTransform = (m: LinearTransform): LinearTransform => {
    const det = m.a * m.d - m.b * m.c
    if (Math.abs(det) < EPSILON) return IDENTITY
    const inverse = { a: m.d / det, b: -m.b / det, c: -m.c / det, d: m.a / det }
    const angle = Math.atan2(m.b, m.a)
    const turn = { a: Math.cos(angle), b: Math.sin(angle), c: -Math.sin(angle), d: Math.cos(angle) }
    return multiplyTransforms(inverse, turn)
}

/**
 * The direction a SIDE's resize cursor points in, for a box drawn through `m`: the way the
 * side actually travels when dragged — along the box's own axis, parallel to the two sides
 * next to it. (A corner points between its sides' normals instead, see cornerNormalAngle.)
 *
 * For a box that is only turned, a side's axis also stands square on that side. For a sheared
 * one (a turned member stretched along with its multiselection) it does not, and the arrow
 * follows the axis, not the square: that is where the side goes.
 *
 * @param travel the side's travel in the box's own space — (1, 0) for the right side,
 *        (0, -1) for the top
 * @returns degrees, 0 = to the right, clockwise like the screen
 */
export const handleTravelAngle = (m: LinearTransform, travel: Point): number => {
    const { x, y } = applyTransform(m, travel)
    return (Math.atan2(y, x) * 180) / Math.PI
}

/**
 * The direction a CORNER's resize cursor points in, for a box drawn through `m`: halfway
 * between the outward normals of the corner's two sides — the way Figma points it, also on a
 * sheared box.
 *
 * @param outward the corner's two sides as outward directions in the box's own space —
 *        (0, -1) and (1, 0) for the top-right corner
 * @returns degrees, 0 = to the right, clockwise like the screen
 */
export const cornerNormalAngle = (m: LinearTransform, outward: Point[]): number => {
    const sum = outward.reduce(
        (total, side) => {
            // the side runs across `side`: along y for a left / right side, x for top / bottom
            const along = applyTransform(m, side.x !== 0 ? { x: 0, y: 1 } : { x: 1, y: 0 })
            const length = Math.hypot(along.x, along.y) || 1
            let normal = { x: -along.y / length, y: along.x / length }
            const out = applyTransform(m, side)
            if (normal.x * out.x + normal.y * out.y < 0) normal = { x: -normal.x, y: -normal.y }
            return { x: total.x + normal.x, y: total.y + normal.y }
        },
        { x: 0, y: 0 }
    )
    return (Math.atan2(sum.y, sum.x) * 180) / Math.PI
}
